import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import ImageEditIcon from "../../assets/Fibo/icons/ImageEditIcon";
import { POSTHOG_EVENTS } from "../../constants/PostHogEvents";
import { usePostHog } from "../../hooks/usePostHog";
import { useAppStore } from "../../hooks/useStores";
import LabResults from "../../layout/LabsLayout/Results/LabResults";
import { PlaygroundImage } from "../../models/playground";
import FiboStyles from "../Fibo/Fibo.module.scss";
import { useFiboController } from "../Fibo/hooks/useFiboController";
import { useScrollToSection } from "../Fibo/hooks/useScrollToSection";
import LabExperiments from "../Labs/Experiments/LabExperiments";
import { PlaygroundVideoProps } from "../Playground/VideoGeneration/Video/PlaygroundVideo";
import FiboEditConfig from "./Config/FiboEditConfig";
import styles from "./FiboEdit.module.scss";
import MaskEditor from "./MaskEditor/MaskEditor";
import { useFiboEditImageUpload } from "./hooks/useFiboEditImageUpload";

const FiboEdit = () => {
	const { registerSection, jumpToSection } = useScrollToSection();
	const { fiboStore, playgroundStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const { handleResultInView, handleJumpTo, handleReferenceImageClick } = useFiboController();
	const { handleUpload } = useFiboEditImageUpload(jumpToSection);
	const { trackEvent } = usePostHog();

	// Track page view
	useEffect(() => {
		trackEvent(POSTHOG_EVENTS.FIBO_EDIT_LAB_PAGE_VIEWED, {
			lab_variant: "FIBO-EDIT",
		});
	}, [trackEvent]);

	return (
		<Box className={FiboStyles.container}>
			<Box className={FiboStyles.mainContent}>
				<Box className={styles.leftContainer}>
					<FiboEditConfig />
				</Box>

				<LabResults
					emptyElement={<EmptyResultsPlaceholderElement />}
					onResultInView={handleResultInView}
					registerSection={registerSection}
					onReferenceImageClick={handleReferenceImageClick}
					onUpload={handleUpload}
				/>
			</Box>

			{playgroundStore.labResultGroups.length > 0 && (
				<LabExperiments
					activeGroupId={playgroundStore.selectedResult?.id || undefined}
					onJumpTo={(resultId: string, selectedImage: PlaygroundImage | PlaygroundVideoProps) =>
						handleJumpTo(resultId, selectedImage, jumpToSection)
					}
				/>
			)}

			<MaskEditor
				open={fiboEditStore.openMaskEditor}
				image={fiboEditStore.config.image}
				onClose={() => {
					fiboEditStore.openMaskEditor = false;
					trackEvent(POSTHOG_EVENTS.FIBO_MASK_EDITOR_CLOSED, {
						lab_variant: "FIBO-EDIT",
					});
				}}
			/>
		</Box>
	);
};

export default observer(FiboEdit);

const EmptyResultsPlaceholderElement = () => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.results.emptyPlaceholder" });

	return (
		<Box className={styles.emptyResultsPlaceholderElement}>
			<ImageEditIcon />
			<Typography className={styles.title}>{t("title")}</Typography>
			<Trans>
				<Typography className={styles.description}>{t("description")}</Typography>
			</Trans>
		</Box>
	);
};
